import type { SearchAppId, SearchHit, SearchResult } from "./Searchtypes";

/* ------------------------------------------------------------------ */
/* Text matching                                                        */
/* ------------------------------------------------------------------ */

export const tokenize = (q: string): string[] =>
  q
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

const words = (t: string): string[] => t.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/** true when a and b differ by at most one edit (insert, delete or swap-free substitution) */
const withinOneEdit = (a: string, b: string): boolean => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (a.length < b.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

/** 0 = no match. Higher is closer: exact, prefix, word prefix, contains, every word, typo-tolerant. */
export const scoreText = (text: string, q: string, tokens: string[]): number => {
  const t = text.toLowerCase();
  if (!t || !tokens.length) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 88;
  const ws = words(t);
  if (tokens.every((tok) => ws.some((w) => w.startsWith(tok)))) return 72;
  if (t.includes(q)) return 58;
  if (tokens.every((tok) => t.includes(tok))) return 42;
  if (tokens.every((tok) => tok.length >= 4 && ws.some((w) => w.length >= 3 && withinOneEdit(w, tok)))) return 30;
  return 0;
};

/* ------------------------------------------------------------------ */
/* Reading a record without knowing its shape                           */
/* ------------------------------------------------------------------ */

const TITLE_KEYS = [
  "title",
  "name",
  "fullName",
  "displayName",
  "accountName",
  "customerName",
  "vendorName",
  "roomName",
  "roomTypeName",
  "categoryName",
  "hotelName",
  "amenityName",
  "subject",
  "label",
  "legalName",
  "description",
  "code",
];
const SUB_KEYS = [
  "subtitle",
  "code",
  "number",
  "accountCode",
  "customerCode",
  "vendorCode",
  "roomNumber",
  "sku",
  "email",
  "phone",
  "status",
  "type",
  "location",
  "stage",
];
const SKIP_KEY = /(^|_)(id|guid|version|rowversion|etag|password|token|hash)$|Id$|^(id|version|rowVersion)$/i;

const isScalar = (v: unknown): v is string | number => typeof v === "string" || typeof v === "number";
const str = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

const humanize = (k: string): string =>
  k
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

export const titleOf = (r: Record<string, unknown>): string => {
  for (const k of TITLE_KEYS) {
    const v = r[k];
    if (isScalar(v) && str(v).trim()) return str(v).trim();
  }
  for (const [k, v] of Object.entries(r)) if (!SKIP_KEY.test(k) && typeof v === "string" && v.trim()) return v.trim();
  return "Untitled";
};

export const subtitleOf = (r: Record<string, unknown>, title: string): string => {
  const seen = new Set<string>([title.toLowerCase()]);
  const out: string[] = [];
  for (const k of SUB_KEYS) {
    const v = r[k];
    if (!isScalar(v)) continue;
    const s = str(v).trim();
    if (!s || seen.has(s.toLowerCase()) || s.length > 80) continue;
    seen.add(s.toLowerCase());
    out.push(s);
    if (out.length === 3) break;
  }
  return out.join(" · ");
};

export const fieldsOf = (r: Record<string, unknown>): [string, string][] => {
  const out: [string, string][] = [];
  for (const [k, v] of Object.entries(r)) {
    if (SKIP_KEY.test(k) || !isScalar(v)) continue;
    const s = str(v).trim();
    if (!s || s.length > 300) continue;
    out.push([humanize(k), s]);
    if (out.length >= 14) break;
  }
  return out;
};

/** best score over the record's fields; label fields count fully, the rest a little less */
export const scoreRecord = (r: Record<string, unknown>, q: string, tokens: string[]): number => {
  let best = 0;
  const hit = new Set<string>();
  for (const [k, v] of Object.entries(r)) {
    if (!isScalar(v) || SKIP_KEY.test(k)) continue;
    const text = str(v);
    if (!text || text.length > 300) continue;
    const w = TITLE_KEYS.includes(k) ? 1 : SUB_KEYS.includes(k) ? 0.8 : 0.55;
    const s = scoreText(text, q, tokens) * w;
    if (s > best) best = s;
    const tl = text.toLowerCase();
    tokens.forEach((tok) => {
      if (tl.includes(tok)) hit.add(tok);
    });
  }
  // words spread over several fields ("john lagos")
  if (best === 0 && tokens.length > 1 && tokens.every((t) => hit.has(t))) best = 30;
  return best;
};

export const toResult = (h: SearchHit, q: string, tokens: string[], currentApp: SearchAppId | null): SearchResult | null => {
  let score = scoreRecord(h.record, q, tokens);
  if (score === 0 && h.trusted) score = 25;
  if (score === 0) return null;
  const title = titleOf(h.record);
  if (h.app === currentApp) score += 25;
  return {
    key: `${h.app}:${h.kind}:${h.id}`,
    app: h.app,
    kind: h.kind,
    title,
    subtitle: subtitleOf(h.record, title),
    score,
    target: h.target,
    fields: fieldsOf(h.record),
  };
};

/* ------------------------------------------------------------------ */
/* Grouping: the app you are in always comes first                      */
/* ------------------------------------------------------------------ */

export interface ResultGroup {
  app: SearchAppId;
  items: SearchResult[];
  best: number;
}

export const groupResults = (results: SearchResult[], currentApp: SearchAppId | null): ResultGroup[] => {
  const map = new Map<SearchAppId, SearchResult[]>();
  for (const r of results) {
    const list = map.get(r.app) ?? [];
    list.push(r);
    map.set(r.app, list);
  }
  const groups: ResultGroup[] = [];
  map.forEach((items, app) => {
    items.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
    groups.push({ app, items, best: items[0].score });
  });
  return groups.sort((a, b) => {
    if (a.app === currentApp) return -1;
    if (b.app === currentApp) return 1;
    return b.best - a.best;
  });
};

/** Splits text into plain and matched parts for highlighting. */
export const highlightParts = (text: string, tokens: string[]): { text: string; on: boolean }[] => {
  const toks = tokens.filter((t) => t.length > 0);
  if (!toks.length || !text) return [{ text, on: false }];
  const lower = text.toLowerCase();
  const mark = new Array<boolean>(text.length).fill(false);
  for (const tok of toks) {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(tok, from);
      if (at < 0) break;
      for (let i = at; i < at + tok.length; i++) mark[i] = true;
      from = at + tok.length;
    }
  }
  const out: { text: string; on: boolean }[] = [];
  let cur = "";
  let on = mark[0] ?? false;
  for (let i = 0; i < text.length; i++) {
    if (mark[i] !== on) {
      out.push({ text: cur, on });
      cur = "";
      on = mark[i];
    }
    cur += text[i];
  }
  if (cur) out.push({ text: cur, on });
  return out;
};